"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"

interface LanyardData {
  discord_user: {
    id: string
    username: string
    discriminator: string
    avatar: string
    global_name: string | null
    display_name: string | null
    display_name_styles?: {
      colors: number[]
      effect_id: number
      font_id: number
    }
    public_flags: number
    bot: boolean
    avatar_decoration_data?: {
      sku_id: string
      asset: string
      expires_at: string | null
    }
    collectibles?: {
      nameplate?: {
        label: string
        sku_id: string
        asset: string
        expires_at: string | null
        palette: string
      }
    }
    clan: any
    primary_guild?: {
      tag: string | null
      identity_guild_id: string
      badge: string | null
      identity_enabled: boolean
    }
  }
  discord_status: "online" | "idle" | "dnd" | "offline"
  activities: Activity[]
  active_on_discord_mobile: boolean
  active_on_discord_desktop: boolean
  active_on_discord_web: boolean
  active_on_discord_embedded: boolean
  listening_to_spotify: boolean
  spotify: SpotifyData | null
  kv: Record<string, string>
}

interface Activity {
  id: string
  name: string
  type: number
  state?: string
  details?: string
  timestamps?: {
    start?: number
    end?: number
  }
  assets?: {
    large_image?: string
    large_text?: string
    small_image?: string
    small_text?: string
  }
  emoji?: {
    id?: string
    name?: string
    animated?: boolean
  }
  application_id?: string
  created_at: number
  session_id?: string
  flags?: number
  sync_id?: string
  party?: {
    id: string
  }
  buttons?: string[]
  platform?: string
}

interface SpotifyData {
  track_id: string
  timestamps: {
    start: number
    end: number
  }
  song: string
  artist: string
  album_art_url: string
  album: string
}

const DISCORD_USER_ID = "877848431983493132"

export function DiscordProfile() {
  const [data, setData] = useState<LanyardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(Date.now())
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    const fetchDiscordData = async () => {
      try {
        const response = await fetch(`https://api.lanyard.rest/v1/users/${DISCORD_USER_ID}`)
        const result = await response.json()

        if (result.success) {
          setData(result.data)
          setError(null)
        } else {
          setError("Failed to fetch Discord data")
        }
      } catch (err) {
        setError("Unable to connect to Discord")
      } finally {
        setIsLoading(false)
      }
    }

    fetchDiscordData()
    const interval = setInterval(fetchDiscordData, 5000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now())
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  const getStatusColor = (status: string) => {
    switch (status) {
      case "online": return { bg: "bg-emerald-500", glow: "shadow-emerald-500/50" }
      case "idle": return { bg: "bg-amber-500", glow: "shadow-amber-500/50" }
      case "dnd": return { bg: "bg-rose-500", glow: "shadow-rose-500/50" }
      default: return { bg: "bg-gray-500", glow: "shadow-gray-500/50" }
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case "online": return "Online"
      case "idle": return "Idle"
      case "dnd": return "Do Not Disturb"
      default: return "Offline"
    }
  }

  const getActivityType = (type: number) => {
    switch (type) {
      case 0: return "Playing"
      case 1: return "Streaming"
      case 2: return "Listening to"
      case 3: return "Watching"
      case 5: return "Competing in"
      default: return "Playing"
    }
  }

  const formatElapsedTime = (timestamp: number) => {
    const elapsed = currentTime - timestamp
    const minutes = Math.floor(elapsed / 60000)
    const hours = Math.floor(minutes / 60)
    const seconds = Math.floor((elapsed % 60000) / 1000)

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} elapsed`
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} elapsed`
  }

  const getSpotifyProgress = () => {
    if (!data?.spotify) return 0
    const duration = data.spotify.timestamps.end - data.spotify.timestamps.start
    const elapsed = currentTime - data.spotify.timestamps.start
    return Math.min((elapsed / duration) * 100, 100)
  }

  const formatSpotifyTime = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const getDiscordBadges = (publicFlags: number) => {
    const badges = []
    if (publicFlags & (1 << 0)) badges.push({ name: 'Discord Employee', icon: 'https://cdn.discordapp.com/badge-icons/5e74e9b61934fc1f67c65515d1f7e60d.png' })
    if (publicFlags & (1 << 1)) badges.push({ name: 'Partnered Server Owner', icon: 'https://cdn.discordapp.com/badge-icons/3f9748e53446a137a052f3454e2de41e.png' })
    if (publicFlags & (1 << 2)) badges.push({ name: 'HypeSquad Events', icon: 'https://cdn.discordapp.com/badge-icons/bf01d1073931f921909045f3a39fd264.png' })
    if (publicFlags & (1 << 3)) badges.push({ name: 'Bug Hunter Level 1', icon: 'https://cdn.discordapp.com/badge-icons/2717692c7dca7289b35297368a940dd0.png' })
    if (publicFlags & (1 << 6)) badges.push({ name: 'HypeSquad Bravery', icon: 'https://cdn.discordapp.com/badge-icons/8a88d63823d8a71cd5e390baa45efa02.png' })
    if (publicFlags & (1 << 7)) badges.push({ name: 'HypeSquad Brilliance', icon: 'https://cdn.discordapp.com/badge-icons/011940fd013da3f7fb926e4a1cd2e618.png' })
    if (publicFlags & (1 << 8)) badges.push({ name: 'HypeSquad Balance', icon: 'https://cdn.discordapp.com/badge-icons/3aa41de486fa12454c3761e8e223442e.png' })
    if (publicFlags & (1 << 9)) badges.push({ name: 'Early Supporter', icon: 'https://cdn.discordapp.com/badge-icons/7060786766c9c840eb3019e725d2b358.png' })
    if (publicFlags & (1 << 14)) badges.push({ name: 'Bug Hunter Level 2', icon: 'https://cdn.discordapp.com/badge-icons/848f79194d4be5ff5f81505cbd0ce1e6.png' })
    if (publicFlags & (1 << 16)) badges.push({ name: 'Verified Bot Developer', icon: 'https://cdn.discordapp.com/badge-icons/6bdc42827a38498929a4920da12695d9.png' })
    if (publicFlags & (1 << 17)) badges.push({ name: 'Early Verified Bot Developer', icon: 'https://cdn.discordapp.com/badge-icons/6bdc42827a38498929a4920da12695d9.png' })
    if (publicFlags & (1 << 18)) badges.push({ name: 'Discord Certified Moderator', icon: 'https://cdn.discordapp.com/badge-icons/fee1624003e2fee35cb398e125dc479b.png' })
    if (publicFlags & (1 << 22)) badges.push({ name: 'Active Developer', icon: 'https://cdn.discordapp.com/badge-icons/6bdc42827a38498929a4920da12695d9.png' })
    return badges
  }

  if (isLoading) {
    return (
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-[#5865f2]/20 via-[#7289da]/20 to-[#5865f2]/20 rounded-2xl blur-xl opacity-75 animate-pulse" />
        <div className="relative bg-[#0c0c0e]/90 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
          <div className="animate-pulse">
            <div className="h-28 bg-gradient-to-br from-[#5865f2]/30 to-[#7289da]/30" />
            <div className="px-5 pb-5">
              <div className="flex items-start gap-4 -mt-12 mb-4">
                <div className="w-24 h-24 rounded-full bg-[#1a1a1d] border-4 border-[#0c0c0e]" />
              </div>
              <div className="space-y-3">
                <div className="h-6 bg-[#1a1a1d] rounded-lg w-2/3" />
                <div className="h-4 bg-[#1a1a1d] rounded-lg w-1/2" />
                <div className="h-24 bg-[#1a1a1d] rounded-xl" />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-rose-500/20 to-red-500/20 rounded-2xl blur-xl opacity-50" />
        <div className="relative bg-[#0c0c0e]/90 backdrop-blur-xl rounded-2xl border border-white/10 p-8">
          <div className="text-center text-[#949ba4] flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-full bg-rose-500/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-sm font-medium">{error || "Unable to load Discord profile"}</p>
          </div>
        </div>
      </div>
    )
  }

  const { discord_user, discord_status, activities, spotify } = data
  const avatarUrl = `https://cdn.discordapp.com/avatars/${discord_user.id}/${discord_user.avatar}.${discord_user.avatar.startsWith('a_') ? 'gif' : 'png'}?size=256`
  const bannerUrl = `https://dcdn.dstn.to/banners/${discord_user.id}`

  const avatarDecorationUrl = discord_user.avatar_decoration_data
    ? `https://cdn.discordapp.com/avatar-decoration-presets/${discord_user.avatar_decoration_data.asset}.png?size=240`
    : null

  const customStatus = activities.find(a => a.type === 4)
  const nonSpotifyActivities = activities.filter(a => a.name !== "Spotify" && a.type !== 4)
  const primaryActivity = nonSpotifyActivities[0]

  const displayNameColor = discord_user.display_name_styles?.colors?.[0]
    ? `#${discord_user.display_name_styles.colors[0].toString(16).padStart(6, '0')}`
    : null

  const userBadges = getDiscordBadges(discord_user.public_flags)
  const statusColors = getStatusColor(discord_status)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative group"
    >
      {/* Glow effect */}
      <motion.div
        className="absolute -inset-1 bg-gradient-to-r from-[#5865f2] via-[#7289da] to-[#5865f2] rounded-2xl blur-xl"
        animate={{
          opacity: isHovered ? 0.4 : 0.2,
          scale: isHovered ? 1.02 : 1
        }}
        transition={{ duration: 0.3 }}
      />

      {/* Main card */}
      <div className="relative bg-[#0c0c0e]/95 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
        {/* Header Banner with gradient overlay */}
        <div className="relative h-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#5865f2] via-[#6574f5] to-[#7289da]" />
          <img
            src={bannerUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
            loading="lazy"
          />
          {/* Banner overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0e] via-transparent to-transparent opacity-60" />

          {/* Live indicator */}
          {discord_status === "online" && (
            <motion.div
              className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm border border-white/10"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <motion.div
                className="w-2 h-2 rounded-full bg-emerald-500"
                animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              <span className="text-[10px] font-medium text-white/90">LIVE</span>
            </motion.div>
          )}
        </div>

        {/* Profile Content */}
        <div className="px-5 pb-5">
          {/* Avatar & Badges Row */}
          <div className="flex items-end justify-between -mt-14 mb-4">
            {/* Avatar */}
            <div className="relative">
              <motion.div
                className={`relative w-24 h-24 rounded-full border-4 border-[#0c0c0e] overflow-hidden bg-[#1a1a1d] shadow-xl ${statusColors.glow} shadow-lg`}
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {discord_user.avatar ? (
                  <img
                    src={avatarUrl}
                    alt={discord_user.username}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#5865f2] to-[#7289da] text-white text-3xl font-bold">
                    {discord_user.username[0].toUpperCase()}
                  </div>
                )}
              </motion.div>

              {/* Avatar Decoration */}
              {avatarDecorationUrl && (
                <motion.img
                  src={avatarDecorationUrl}
                  alt=""
                  className="absolute inset-0 w-24 h-24 pointer-events-none object-contain"
                  style={{ margin: '-4px' }}
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              )}

              {/* Status Indicator */}
              <motion.div
                className={`absolute bottom-1 right-1 w-6 h-6 rounded-full border-4 border-[#0c0c0e] ${statusColors.bg}`}
                animate={discord_status === "online" ? {
                  boxShadow: ["0 0 0 0 rgba(34, 197, 94, 0)", "0 0 0 8px rgba(34, 197, 94, 0.3)", "0 0 0 0 rgba(34, 197, 94, 0)"]
                } : {}}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>

            {/* Badges */}
            <div className="flex items-center gap-1 flex-wrap justify-end max-w-[60%] pb-2">
              {userBadges.map((badge, idx) => (
                <motion.div
                  key={idx}
                  className="group/badge relative"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ scale: 1.15, y: -2 }}
                >
                  <img
                    src={badge.icon}
                    alt={badge.name}
                    className="w-5 h-5 drop-shadow-lg"
                  />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 bg-black/90 backdrop-blur-sm text-white text-[10px] rounded-lg whitespace-nowrap opacity-0 group-hover/badge:opacity-100 transition-all pointer-events-none z-20 shadow-xl border border-white/10">
                    {badge.name}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-black/90" />
                  </div>
                </motion.div>
              ))}

              {/* Platform Indicators */}
              <div className="flex gap-1 ml-1">
                {data.active_on_discord_desktop && (
                  <motion.div
                    className="w-5 h-5 rounded-md bg-white/5 flex items-center justify-center group/platform relative"
                    whileHover={{ scale: 1.1, backgroundColor: "rgba(255,255,255,0.1)" }}
                  >
                    <svg className="w-3.5 h-3.5 text-[#b5bac1]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 3H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h3l-1 1v2h12v-2l-1-1h3c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 13H4V5h16v11z" />
                    </svg>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black/90 text-white text-[10px] rounded-lg opacity-0 group-hover/platform:opacity-100 transition-all pointer-events-none z-20">Desktop</div>
                  </motion.div>
                )}
                {data.active_on_discord_mobile && (
                  <motion.div
                    className="w-5 h-5 rounded-md bg-white/5 flex items-center justify-center group/platform relative"
                    whileHover={{ scale: 1.1, backgroundColor: "rgba(255,255,255,0.1)" }}
                  >
                    <svg className="w-3.5 h-3.5 text-[#b5bac1]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M15.5 1h-8A2.5 2.5 0 005 3.5v17A2.5 2.5 0 007.5 23h8a2.5 2.5 0 002.5-2.5v-17A2.5 2.5 0 0015.5 1zm-4 21c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4.5-4H7V4h9v14z" />
                    </svg>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black/90 text-white text-[10px] rounded-lg opacity-0 group-hover/platform:opacity-100 transition-all pointer-events-none z-20">Mobile</div>
                  </motion.div>
                )}
                {data.active_on_discord_web && (
                  <motion.div
                    className="w-5 h-5 rounded-md bg-white/5 flex items-center justify-center group/platform relative"
                    whileHover={{ scale: 1.1, backgroundColor: "rgba(255,255,255,0.1)" }}
                  >
                    <svg className="w-3.5 h-3.5 text-[#b5bac1]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                    </svg>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black/90 text-white text-[10px] rounded-lg opacity-0 group-hover/platform:opacity-100 transition-all pointer-events-none z-20">Web</div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="mb-4">
            <h3
              className="text-2xl font-bold leading-tight mb-1 bg-clip-text"
              style={displayNameColor
                ? { color: displayNameColor }
                : { background: "linear-gradient(135deg, #fff 0%, #b5bac1 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }
              }
            >
              {discord_user.display_name || discord_user.global_name || discord_user.username}
            </h3>
            <p className="text-sm text-[#949ba4] font-medium">
              @{discord_user.username}
            </p>
          </div>

          {/* Separator */}
          <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-4" />

          {/* Custom Status */}
          <AnimatePresence>
            {customStatus && customStatus.state && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-4"
              >
                <div className="flex items-start gap-2.5 bg-white/5 rounded-xl p-3.5 border border-white/5">
                  {customStatus.emoji && (
                    customStatus.emoji.id ? (
                      <img
                        src={`https://cdn.discordapp.com/emojis/${customStatus.emoji.id}.${customStatus.emoji.animated ? 'gif' : 'png'}`}
                        alt={customStatus.emoji.name || ''}
                        className="w-5 h-5 flex-shrink-0"
                      />
                    ) : (
                      <span className="text-lg leading-5">{customStatus.emoji.name}</span>
                    )
                  )}
                  <p className="text-sm text-[#e4e5e7] break-words leading-relaxed">
                    {customStatus.state}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Activity */}
          <AnimatePresence>
            {primaryActivity && (
              <motion.div
                className="mb-4"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ delay: 0.1 }}
              >
                <h4 className="text-[10px] font-bold text-[#949ba4] mb-2 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#5865f2] animate-pulse" />
                  {getActivityType(primaryActivity.type)}
                </h4>
                <motion.div
                  className="bg-white/5 rounded-xl p-4 border border-white/5 hover:border-white/10 transition-colors"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-start gap-3">
                    {primaryActivity.assets?.large_image && (
                      <div className="relative flex-shrink-0">
                        <img
                          src={
                            primaryActivity.assets.large_image.startsWith('mp:')
                              ? `https://media.discordapp.net/${primaryActivity.assets.large_image.replace('mp:', '')}`
                              : `https://cdn.discordapp.com/app-assets/${primaryActivity.application_id}/${primaryActivity.assets.large_image}.png`
                          }
                          alt={primaryActivity.name}
                          className="w-16 h-16 rounded-xl object-cover shadow-lg"
                        />
                        {primaryActivity.assets?.small_image && (
                          <img
                            src={`https://cdn.discordapp.com/app-assets/${primaryActivity.application_id}/${primaryActivity.assets.small_image}.png`}
                            alt=""
                            className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-[#0c0c0e] shadow-lg"
                          />
                        )}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white mb-1 truncate">
                        {primaryActivity.name}
                      </p>
                      {primaryActivity.details && (
                        <p className="text-xs text-[#b5bac1] truncate mb-0.5">
                          {primaryActivity.details}
                        </p>
                      )}
                      {primaryActivity.state && (
                        <p className="text-xs text-[#949ba4] truncate">
                          {primaryActivity.state}
                        </p>
                      )}
                      {primaryActivity.timestamps?.start && (
                        <p className="text-xs text-[#5865f2] mt-2 font-medium">
                          {formatElapsedTime(primaryActivity.timestamps.start)}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Spotify */}
          <AnimatePresence>
            {spotify && (
              <motion.div
                className="mb-4"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ delay: 0.2 }}
              >
                <h4 className="text-[10px] font-bold text-[#949ba4] mb-2 uppercase tracking-widest flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-[#1db954]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                  </svg>
                  Listening to Spotify
                </h4>
                <motion.div
                  className="bg-gradient-to-br from-[#1db954]/10 to-[#191414]/50 rounded-xl p-4 border border-[#1db954]/20 hover:border-[#1db954]/40 transition-colors"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-start gap-4 mb-3">
                    {spotify.album_art_url && (
                      <motion.img
                        src={spotify.album_art_url}
                        alt={spotify.album}
                        className="w-16 h-16 rounded-lg object-cover flex-shrink-0 shadow-xl"
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                        style={{ animationPlayState: "running" }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <a
                        href={`https://open.spotify.com/track/${spotify.track_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-bold text-white hover:text-[#1db954] transition-colors truncate block"
                      >
                        {spotify.song}
                      </a>
                      <p className="text-xs text-[#b5bac1] truncate mt-0.5">
                        {spotify.artist}
                      </p>
                      <p className="text-xs text-[#949ba4] truncate">
                        {spotify.album}
                      </p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-1.5">
                    <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                      <motion.div
                        className="bg-[#1db954] h-full rounded-full"
                        style={{ width: `${getSpotifyProgress()}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-[10px] text-[#b5bac1] font-medium">
                      <span>{formatSpotifyTime(currentTime - spotify.timestamps.start)}</span>
                      <span>{formatSpotifyTime(spotify.timestamps.end - spotify.timestamps.start)}</span>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Status Footer */}
          <div className="pt-3 border-t border-white/5">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${statusColors.bg}`} />
                <span className="text-[#b5bac1] font-medium">{getStatusText(discord_status)}</span>
              </div>
              <span className="text-[#5c5e66]">
                Member since Aug 2021
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
